/**
 * Stato persistente del player (IndexedDB): token, manifest + etag, ultimo offset dell'orologio,
 * contabilità del GC degli asset. Wrapper minimale scritto a mano, nessuna dipendenza.
 */

export interface KvStore {
  get<T>(key: string): Promise<T | undefined>;
  set(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
}

export class MemoryKvStore implements KvStore {
  private readonly m = new Map<string, unknown>();
  async get<T>(key: string): Promise<T | undefined> { return this.m.get(key) as T | undefined; }
  async set(key: string, value: unknown): Promise<void> { this.m.set(key, structuredCloneSafe(value)); }
  async delete(key: string): Promise<void> { this.m.delete(key); }
  async clear(): Promise<void> { this.m.clear(); }
}

function structuredCloneSafe<T>(v: T): T {
  return v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T);
}

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('IndexedDB error'));
  });
}

const STORE = 'kv';

export class IdbKvStore implements KvStore {
  private constructor(private readonly db: IDBDatabase) {}

  static async open(name = 'hoox-player', factory: IDBFactory = indexedDB): Promise<IdbKvStore> {
    const openReq = factory.open(name, 1);
    openReq.onupgradeneeded = () => {
      if (!openReq.result.objectStoreNames.contains(STORE)) openReq.result.createObjectStore(STORE);
    };
    return new IdbKvStore(await req(openReq));
  }

  private store(mode: IDBTransactionMode): IDBObjectStore {
    return this.db.transaction(STORE, mode).objectStore(STORE);
  }

  async get<T>(key: string): Promise<T | undefined> {
    return (await req(this.store('readonly').get(key))) as T | undefined;
  }
  async set(key: string, value: unknown): Promise<void> {
    await req(this.store('readwrite').put(value, key));
  }
  async delete(key: string): Promise<void> {
    await req(this.store('readwrite').delete(key));
  }
  async clear(): Promise<void> {
    await req(this.store('readwrite').clear());
  }
}

/** Apre IndexedDB; se non disponibile (modalità privata, policy) ripiega sulla memoria. */
export async function openKv(): Promise<{ kv: KvStore; persistent: boolean }> {
  try {
    return { kv: await IdbKvStore.open(), persistent: true };
  } catch {
    return { kv: new MemoryKvStore(), persistent: false };
  }
}

// --- accesso tipizzato ------------------------------------------------------

export interface StoredManifest {
  manifest: unknown; // JSON grezzo; si rivalida con sanitizeManifest al caricamento
  etag: string | null;
}

const K = {
  token: 'token',
  device: 'device',
  manifest: 'manifest',
  clockOffset: 'clockOffset',
  unreferencedSince: 'unreferencedSince',
} as const;

export class PlayerState {
  constructor(readonly kv: KvStore) {}

  getToken(): Promise<string | undefined> { return this.kv.get<string>(K.token); }
  setToken(token: string): Promise<void> { return this.kv.set(K.token, token); }
  clearToken(): Promise<void> { return this.kv.delete(K.token); }

  getDevice(): Promise<{ id: number; name: string } | undefined> { return this.kv.get(K.device); }
  setDevice(d: { id: number; name: string }): Promise<void> { return this.kv.set(K.device, d); }

  getManifest(): Promise<StoredManifest | undefined> { return this.kv.get<StoredManifest>(K.manifest); }
  setManifest(m: StoredManifest): Promise<void> { return this.kv.set(K.manifest, m); }
  clearManifest(): Promise<void> { return this.kv.delete(K.manifest); }

  getClockOffset(): Promise<number | undefined> { return this.kv.get<number>(K.clockOffset); }
  setClockOffset(ms: number): Promise<void> { return this.kv.set(K.clockOffset, ms); }

  async getUnreferencedSince(): Promise<Record<string, number>> {
    return (await this.kv.get<Record<string, number>>(K.unreferencedSince)) ?? {};
  }
  setUnreferencedSince(v: Record<string, number>): Promise<void> { return this.kv.set(K.unreferencedSince, v); }

  /** Torna al pairing: via token, manifest, offset, contabilità GC. */
  async resetAll(): Promise<void> { await this.kv.clear(); }
}
